import React, { useContext, useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import type {
  Candidate,
  Experience,
  Taxonomy,
  TaxonomyFocusAreaEnum,
} from '@axiom/validation';
import {
  Button,
  DateUtil,
  Gutter,
  Layout,
  Paragraph,
  SectionHeader,
  Well,
} from '@axiom/ui';

import { ExperienceApi } from '../../api/experience-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';

import {
  ExperienceEditSectionKeys,
  ExperienceEditUtil,
  minimumDescriptionLengthToRetrieveSuggestions,
} from './experience-edit-util';
import type { ExperienceSectionStateType } from './ExperienceEditContext';
import { ExperienceEditContext } from './ExperienceEditContext';
import { ExperienceEditSection } from './ExperienceEditSection';
import { ExperienceEditFormFocusAreasSort } from './ExperienceEditFormFocusAreasSort';
import { ExperienceEditFormFocusAreasSelect } from './ExperienceEditFormFocusAreasSelect';

const { FOCUS_AREAS, SKILLS } = ExperienceEditSectionKeys;

export type ExperienceEditFormFocusAreasProps = {
  candidate: Candidate;
  expandable?: boolean;
  experience: Experience;
  taxonomy: Taxonomy;
  name?: string;
};

export const ExperienceEditFormFocusAreas = ({
  candidate,
  expandable,
  experience,
  taxonomy,
}: ExperienceEditFormFocusAreasProps) => {
  const {
    accordionState,
    setAccordionState,
    dirtyFormState,
    setDirtyFormState,
  } = useContext(ExperienceEditContext);
  const initialFocusAreas =
    experience.experiencesFocusAreaOrderPreferences?.[0]?.focusAreas ?? [];
  const [focusAreasToSort, setFocusAreasToSort] =
    useState<TaxonomyFocusAreaEnum[]>(initialFocusAreas);
  const [isEditingFocusAreas, setIsEditingFocusAreas] = useState(false);

  useEffect(() => {
    setFocusAreasToSort(initialFocusAreas);
  }, [experience]);

  useEffect(() => {
    const isDirty = !isEqual(initialFocusAreas, focusAreasToSort);

    if (dirtyFormState[FOCUS_AREAS] !== isDirty) {
      setDirtyFormState((prevState: ExperienceSectionStateType) => ({
        ...prevState,
        [FOCUS_AREAS]: isDirty,
      }));
    }
  }, [focusAreasToSort, initialFocusAreas]);

  return (
    <ExperienceEditSection
      expandable={expandable}
      expanded={accordionState[FOCUS_AREAS]}
      isComplete={ExperienceEditUtil.isFocusAreasComplete(experience)}
      title="Focus Areas"
      name={FOCUS_AREAS}
    >
      {focusAreasToSort.length === 0 ? (
        <Well background="blue" name="EXPERIENCE_EDIT_FORM_SELECT_INTRO">
          <SectionHeader>Add focus areas</SectionHeader>
          <Gutter bottom="8px" />
          <Paragraph>
            Select the focus areas you worked in during this engagement. This
            helps Axiom really understand all the types of work you did at this
            client, and better match you in the future.
          </Paragraph>
          <Gutter bottom="16px" />
          <Layout position="right">
            <Button
              pattern="secondary"
              name="EXPERIENCE_EDIT_FORM_ADD_FOCUS_AREAS"
              onClick={async () => {
                if (
                  !experience.suggestedFocusAreasRetrievedAt &&
                  (experience.description?.length ?? 0) >=
                    minimumDescriptionLengthToRetrieveSuggestions
                ) {
                  await ExperienceApi.createExperienceFocusAreaPredictions(
                    experience.id
                  );
                  await CandidateExperienceApi.refreshExperiences(candidate.id);
                }
                setIsEditingFocusAreas(true);
              }}
            >
              Add focus areas
            </Button>
          </Layout>
        </Well>
      ) : (
        <ExperienceEditFormFocusAreasSort
          candidateId={candidate.id}
          experience={experience}
          focusAreasToSort={focusAreasToSort}
          name="EXPERIENCE_EDIT_FORM_FOCUS_AREAS_SORT"
          onEditFocusAreasClicked={() => {
            setIsEditingFocusAreas(true);
          }}
          setFocusAreasToSort={setFocusAreasToSort}
          taxonomy={taxonomy}
        />
      )}

      {isEditingFocusAreas ? (
        <ExperienceEditFormFocusAreasSelect
          candidate={candidate}
          experience={experience}
          focusAreas={focusAreasToSort}
          name="EXPERIENCE_EDIT_FORM_SELECT_FORM"
          onFocusAreasSelected={async (focusAreas, noFocusAreasApply) => {
            if (noFocusAreasApply ?? false) {
              await Promise.all([
                ExperienceApi.updateExperience(experience.id, {
                  candidateId: candidate.id,
                  noFocusAreasApplyMarkedAt: DateUtil.formatAsTimestamp(
                    new Date()
                  ),
                }),
                ExperienceApi.updateCandidateExperienceTaxonomy(
                  candidate.id,
                  experience.id,
                  []
                ),
              ]);
              await CandidateExperienceApi.refreshExperiences(candidate.id);
              setAccordionState({
                ...ExperienceEditUtil.setAllToFalse(accordionState),
                [SKILLS]: true,
              });
            }
            setFocusAreasToSort(focusAreas);
            setIsEditingFocusAreas(false);
          }}
          taxonomy={taxonomy}
        />
      ) : null}
    </ExperienceEditSection>
  );
};
